import * as React from 'react';
import Calendar from '../../Calendar/Calendar';
import '../DatePicker.scss';
import { cn } from '../../../utils';

type Props = {
  startDate: Date;
  endDate: Date;
  calendarProps?: [
    React.ComponentProps<typeof Calendar>,
    React.ComponentProps<typeof Calendar>,
  ];
  onChange?: (date: [Date, Date] | null) => void;
  setDateSelected?: (d: 'start' | 'end') => void;
};

export function DateRangePicker({
  startDate,
  endDate,
  calendarProps,
  setDateSelected,
  onChange,
}: Props) {
  return (
    <div className='sui-flex flex-row sui-gap-3'>
      <Calendar
        selected={startDate}
        maxDate={endDate}
        onChange={(date) => {
          onChange?.([date, endDate]);
          setDateSelected?.('start');
        }}
        data-testid='snap-ui-calendar-range-start'
        renderDayContents={(day, date) =>
          renderDayContents(day, date, startDate, endDate, 'start')
        }
        {...calendarProps?.[0]}
      />
      <Calendar
        selected={endDate}
        minDate={startDate}
        onChange={(date) => {
          onChange?.([startDate, date]);
          setDateSelected?.('end');
        }}
        data-testid='snap-ui-calendar-range-end'
        renderDayContents={(day, date) =>
          renderDayContents(day, date, startDate, endDate, 'end')
        }
        {...calendarProps?.[1]}
      />
    </div>
  );
}

const isInRange = (date: Date, start: Date, end: Date) => {
  if (!start || !end) return false;
  return (
    date >= new Date(start.setHours(0, 0, 0, 0)) &&
    date <= new Date(end.setHours(0, 0, 0, 0))
  );
};

/**
 * Behaviors:
 * - Fade left if the date is the first of the month and not Sunday
 * - Fade right if the date is the last of the month and not Saturday
 * - Highlight the start date and end date with a blue circle
 * - Highlight the range between the start date and end date with a light blue background
 * - Make sure that the start/end days continue the light blue background only until their centers
 * - When looking at start date calendar, the end date should be grayed out, and vice versa.
 */
const renderDayContents = (
  day: number,
  date: Date,
  startDate: Date | undefined,
  endDate: Date | undefined,
  dateSelector: 'start' | 'end'
) => {
  const isFirstOfMonth = date.getDate() === 1;
  const isLastOfMonth =
    new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate() === date.getDate();
  const isNotSunday = date.getDay() !== 0; // 0 represents Sunday
  const isNotSaturday = date.getDay() !== 6; // 6 represents Saturday
  const isStartDate = date.toDateString() === startDate?.toDateString();
  const isEndDate = date.toDateString() === endDate?.toDateString();
  const inRange = isInRange(date, startDate, endDate);
  const fadeLeft = isFirstOfMonth && isNotSunday;
  const fadeRight = isLastOfMonth && isNotSaturday;

  // Styles for the background of the range
  const rangeBackgroundStyle = {
    ...(isStartDate && { right: '0' }),
    ...(isEndDate && { left: '0' }),
  };

  // Classnames for the range background
  const rangeBackgroundClass = cn(
    'sui-absolute sui-top-0 sui-left-0 sui-h-full sui-w-full sui-z-0',
    fadeLeft ? 'fade-left' : fadeRight ? 'fade-right' : 'sui-bg-blue-2/50',
    (isStartDate || isEndDate) &&
      'sui-rounded-[50%] sui-w-[24px] sui-h-[40px] sui-bg-blue-2/50 sui-absolute sui-rounded-none'
  );

  // Classnames for the day circle (start or end date)
  const dayCircleClass = cn(
    'sui-w-[40px] sui-h-[40px] sui-rounded-[50%] sui-z-10 sui-flex sui-items-center sui-justify-center',
    dateSelector === 'start' && isStartDate && 'sui-bg-blue-4/100  sui-text-white',
    dateSelector === 'start' && isEndDate && 'sui-bg-gray-3/100',
    dateSelector === 'end' && isEndDate && 'sui-bg-blue-4/100  sui-text-white',
    dateSelector === 'end' && isStartDate && 'sui-bg-gray-3/100'
  );

  // Classnames for the selected day
  const defaultDayClass = 'sui-z-10';

  return (
    <div
      className={
        'sui-relative sui-w-[40px] sui-h-[40px] sui-flex sui-items-center sui-justify-center'
      }
    >
      {inRange && <div style={rangeBackgroundStyle} className={rangeBackgroundClass} />}
      {isStartDate || isEndDate ? (
        <div className={dayCircleClass}>{day}</div>
      ) : (
        <div className={defaultDayClass}>{day}</div>
      )}
    </div>
  );
};
